import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, text, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsHighPrecision } from '../../custom-type'
import { organization } from '../auth'
import { File } from '../file'
import { RoyaltyToBooks } from './royalty-to-books'
import { RoyaltyTransaction } from './royalty-transaction'

export const Royalty = pgTable(
  'royalty',
  {
    id: uuid().primaryKey().defaultRandom(),
    organizationId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'restrict' }),
    periodYear: integer().notNull(),
    periodMonth: integer().notNull(),
    totalAmount: numericAsHighPrecision().notNull(),
    /** Total pool amount for the period (uniquePremiums * pool amount). Used to compute partner share %. */
    sharedAmount: numericAsHighPrecision(),
    currency: text().notNull().default('IDR'),
    status: text({ enum: ['PENDING', 'CALCULATED', 'PAID', 'CANCELLED'] })
      .notNull()
      .default('PENDING'),
    calculatedAt: timestamp({ mode: 'date', withTimezone: true }),
    paidAt: timestamp({ mode: 'date', withTimezone: true }),
    csvFileId: uuid().references(() => File.id, { onDelete: 'restrict' }),
    description: text(),
    royaltyProgramVersion: text(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    uniqueIndex('royalty_organization_period_uidx').on(t.organizationId, t.periodYear, t.periodMonth),
    index('royalty_organization_idx').on(t.organizationId),
    index('royalty_status_idx').on(t.status),
    index('royalty_calculated_at_idx').on(t.calculatedAt),
  ],
)

export const royaltyRelations = relations(Royalty, ({ one, many }) => ({
  organization: one(organization, {
    fields: [Royalty.organizationId],
    references: [organization.id],
  }),
  royaltyToBooks: many(RoyaltyToBooks),
  transactions: many(RoyaltyTransaction),
  csvFile: one(File, {
    fields: [Royalty.csvFileId],
    references: [File.id],
  }),
}))

export const InsertRoyaltySchema = createInsertSchema(Royalty).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateRoyaltySchema = createUpdateSchema(Royalty).partial().omit({
  id: true,
  organizationId: true,
  periodYear: true,
  periodMonth: true,
  createdAt: true,
  updatedAt: true,
})

export const SelectRoyaltySchema = createSelectSchema(Royalty)

export type InsertRoyalty = typeof Royalty.$inferInsert
export type UpdateRoyalty = z.infer<typeof UpdateRoyaltySchema>
export type SelectRoyalty = typeof Royalty.$inferSelect
